import React, { useState, useEffect } from 'react';
import { DollarSign, X, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import api from '../services/api';

const PreofertaModal = ({ isOpen, onClose, lote, onSuccess }) => {
    const [formData, setFormData] = useState({
        monto: '',
        cabezas: '',
        formaPago: '1' // Default ID for Contado or similar, adjust as needed
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [step, setStep] = useState(0);
    const [minMonto, setMinMonto] = useState(0);

    // Determinar step y monto mínimo al abrir o cambiar lote
    useEffect(() => {
        if (lote) {
            const isKilos = lote.tipo_venta === 'K';
            const currentStep = isKilos ? 0.01 : 5;
            const currentMax = lote.max_preoferta || 0;
            const nextMin = currentMax + currentStep;

            setStep(currentStep);
            setMinMonto(nextMin);

            // Inicializar formulario
            setFormData(prev => ({
                ...prev,
                monto: nextMin.toFixed(isKilos ? 2 : 0),
                cabezas: lote.cabezas || ''
            }));
        }
    }, [lote]);

    if (!isOpen) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Validar monto
            if (parseFloat(formData.monto) < minMonto) {
                throw new Error(`El monto debe ser al menos ${minMonto}`);
            }

            // Obtener usuario (asumimos que ya está logueado si llegó aquí)
            const user = JSON.parse(localStorage.getItem('plaza_user'));
            if (!user) {
                throw new Error("Usuario no identificado");
            }

            const data = {
                lote_id: lote.id,
                usuario_id: user.id,
                monto: parseFloat(formData.monto),
                cabezas: parseInt(formData.cabezas),
                forma_pago_id: parseInt(formData.formaPago)
            };

            await api.preofertar(data);

            setSuccess(true);
            setTimeout(() => {
                if (onSuccess) onSuccess();
                onClose();
                setSuccess(false); // Reset for next time
            }, 2000);

        } catch (err) {
            console.error(err);
            setError(err.message || 'Error al enviar la preoferta');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
                <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" />
                <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-8 text-center animate-fade-in-up">
                    <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
                        <CheckCircle2 className="h-10 w-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">¡Preoferta Enviada!</h3>
                    <p className="text-gray-500">Su oferta ha sido registrada correctamente.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity" onClick={onClose} />

            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden transform transition-all animate-fade-in-up">
                {/* Header */}
                <div className="bg-gradient-to-r from-green-900 to-green-800 p-6 relative">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>

                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <DollarSign className="w-6 h-6" />
                        Realizar Preoferta
                    </h2>
                    <p className="text-green-100 text-sm mt-1">
                        Lote {lote?.numero_lote} - {lote?.categoria}
                    </p>
                </div>

                <div className="p-6">
                    <form className="space-y-5" onSubmit={handleSubmit}>
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
                                <AlertCircle className="w-4 h-4" />
                                <span className="block sm:inline">{error}</span>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4">
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Monto ({lote?.tipo_venta === 'K' ? 'US$ / Kg' : 'US$ / Cabeza'})
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <span className="text-gray-500 sm:text-sm">$</span>
                                    </div>
                                    <input
                                        type="number"
                                        step={step}
                                        min={minMonto}
                                        required
                                        value={formData.monto}
                                        onChange={(e) => setFormData({ ...formData, monto: e.target.value })}
                                        className="block w-full pl-7 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500 text-lg font-bold text-gray-900"
                                    />
                                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                        <span className="text-gray-500 sm:text-sm">
                                            Min: {minMonto.toFixed(lote?.tipo_venta === 'K' ? 2 : 0)}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Cabezas
                                </label>
                                <input
                                    type="number"
                                    required
                                    value={formData.cabezas}
                                    onChange={(e) => setFormData({ ...formData, cabezas: e.target.value })}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Forma de Pago
                                </label>
                                <select
                                    value={formData.formaPago}
                                    onChange={(e) => setFormData({ ...formData, formaPago: e.target.value })}
                                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-green-500 focus:border-green-500"
                                >
                                    <option value="1">Contado</option>
                                    <option value="2">90 Días</option>
                                    <option value="3">180 Días</option>
                                </select>
                            </div>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-600">
                            <p>Al confirmar, usted se compromete a adquirir el lote por el monto ofertado si resulta ganador.</p>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-md text-sm font-bold text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all transform hover:scale-[1.02]"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="animate-spin -ml-1 mr-2 h-5 w-5" />
                                    Enviando...
                                </>
                            ) : (
                                'Confirmar Preoferta'
                            )}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PreofertaModal;
